import type { ForumPost } from '@mtc/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RichContent } from '../components/RichContent';
import { StateMessage } from '../components/StateMessage';
import { useForumFeed, useFormations, usePublicForumFeed } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../lib/auth';

export function Publications() {
  const navigate = useNavigate();
  const { user, member, isLoading, isVerified } = useAuth();

  useSEO({
    title: 'Publications — MTC',
    description: 'Articles scientifiques et actualités du Moroccan Training Center.',
  });

  const enrolledIds = member?.enrolledFormationIds ?? [];
  const isMember = !!user && isVerified && enrolledIds.length > 0;

  // Enrolled members get the full feed (public + all + their formations).
  // Everyone else (anonymous, unverified, or not-yet-enrolled) gets the public
  // 'Grand public' posts only.
  const memberFeedQ = useForumFeed(isMember ? user?.uid : undefined, enrolledIds);
  const publicFeedQ = usePublicForumFeed(!isMember);
  const formationsQ = useFormations();

  const titleBySlug = useMemo(() => {
    const m: Record<string, string> = {};
    (formationsQ.data ?? []).forEach((f) => {
      m[f.id] = f.titre;
    });
    return m;
  }, [formationsQ.data]);

  // Tag only the members-only posts (signals exclusivity). Public posts get no
  // chip — everything an anonymous visitor sees is public, so it'd be noise.
  const tagFor = (p: ForumPost): string | null => {
    if (p.audience === 'public') return null;
    if (p.audience === 'all') return 'Réservé aux membres';
    const names = p.formationIds.map((id) => titleBySlug[id] ?? id);
    return names.length > 0 ? `Réservé · ${names.join(', ')}` : 'Réservé aux membres';
  };

  if (isLoading) return <StateMessage variant="loading" />;

  const feedQ = isMember ? memberFeedQ : publicFeedQ;
  const posts = feedQ.data ?? [];

  return (
    <div style={{ maxWidth: 820, margin: '64px auto 96px', padding: '0 24px' }}>
      <header style={{ marginBottom: 24 }}>
        <span className="mtc-eyebrow">{isMember ? 'Espace membre' : 'Ressources'}</span>
        <h1
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 'clamp(32px, 4vw, 52px)',
            fontWeight: 400,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            margin: '8px 0 4px',
          }}
        >
          Publications
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(15,25,22,0.6)', margin: 0 }}>
          Articles scientifiques et actualités partagés par l'équipe MTC.
        </p>
      </header>

      {/* Nudge for non-members: there's more behind sign-in. */}
      {!isMember && (
        <div
          style={{
            marginBottom: 24,
            padding: '14px 18px',
            background: 'rgba(11,93,59,0.06)',
            border: '1px solid rgba(11,93,59,0.18)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 14, color: 'rgba(15,25,22,0.75)' }}>
            {user
              ? 'Des publications supplémentaires sont réservées aux participants inscrits.'
              : 'Connectez-vous pour accéder aux publications réservées aux participants.'}
          </span>
          <button
            type="button"
            className="mtc-btn mtc-btn-primary"
            style={{ flexShrink: 0 }}
            onClick={() => navigate(user ? '/mon-espace' : '/connexion')}
          >
            {user ? 'Mon espace' : 'Se connecter'}
          </button>
        </div>
      )}

      {feedQ.isLoading ? (
        <StateMessage variant="loading" />
      ) : posts.length === 0 ? (
        <Notice
          title="Rien pour le moment"
          body="Aucune publication n'a encore été partagée. Revenez bientôt !"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {posts.map((p) => (
            <PostCard key={p.id} post={p} tag={tagFor(p)} />
          ))}
        </div>
      )}
    </div>
  );
}

function toExcerpt(html: string, max = 220): string {
  const text = (html ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, '') + '…';
}

function PostCard({ post, tag }: { post: ForumPost; tag?: string | null }) {
  // Messages are short → shown in full. Articles collapse to an excerpt so the
  // feed stays scannable when there are many.
  const [expanded, setExpanded] = useState(post.kind !== 'article');
  const excerpt = useMemo(() => toExcerpt(post.body), [post.body]);

  return (
    <article
      style={{
        background: 'white',
        border: '1px solid rgba(15,25,22,0.1)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {post.coverImageUrl && (
        <img
          src={post.coverImageUrl}
          alt=""
          style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }}
        />
      )}
      <div style={{ padding: '22px 24px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 8,
          }}
        >
          {post.pinned && (
            <span style={{ fontSize: 12, color: '#0B5D3B' }} title="Épinglé">
              📌 Épinglé
            </span>
          )}
          <span
            style={{
              fontFamily: 'var(--mtc-font-mono)',
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(15,25,22,0.45)',
            }}
          >
            {post.kind === 'article' ? 'Article' : 'Message'} ·{' '}
            {new Date(post.createdAt).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
          {tag && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 9px',
                borderRadius: 999,
                background: 'rgba(11,93,59,0.1)',
                color: '#0B5D3B',
              }}
            >
              {tag}
            </span>
          )}
        </div>
        <h2
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 26,
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0 0 14px',
          }}
        >
          {post.title}
        </h2>

        {expanded ? (
          <RichContent html={post.body} />
        ) : (
          <p style={{ fontSize: 15, lineHeight: 1.6, color: 'rgba(15,25,22,0.75)', margin: 0 }}>
            {excerpt}
          </p>
        )}

        {post.kind === 'article' && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              marginTop: 14,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#0B5D3B',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {expanded ? 'Réduire ↑' : "Lire l'article →"}
          </button>
        )}

        {expanded && post.attachments.length > 0 && (
          <div style={{ marginTop: 18, borderTop: '1px solid rgba(15,25,22,0.08)', paddingTop: 14 }}>
            <div
              style={{
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 10,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'rgba(15,25,22,0.5)',
                marginBottom: 8,
              }}
            >
              Pièces jointes
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {post.attachments.map((a) => (
                <li key={a.url}>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0B5D3B', fontSize: 14, textDecoration: 'none' }}
                  >
                    📄 {a.name} ↗
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}

function Notice({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div
      style={{
        padding: '32px 24px',
        border: '1px dashed rgba(15,25,22,0.2)',
        borderRadius: 12,
        textAlign: 'center',
        color: 'rgba(15,25,22,0.7)',
      }}
    >
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px', color: 'var(--mtc-ink)' }}>
        {title}
      </h2>
      <p style={{ fontSize: 14, margin: '0 0 16px', maxWidth: 420, marginInline: 'auto' }}>{body}</p>
      {action && (
        <button type="button" className="mtc-btn mtc-btn-primary" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}
